import * as React from 'react';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import {
  ContentLoader,
  Form,
  FormCheckboxGroup,
  FormSelect,
  FormTextField,
  Grid,
  IOption,
  IPagedResultsV1,
  requiredRule,
  UseFormReturn,
} from '@gcl/react-core';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { useGetProjectUserProfileOptionsByCompanyAsync } from '@userProfile/projectUserProfile.queries';
import {
  useGetProjectMasterScheduleOptionsForAssignT1TeamsAsync,
  useUpsertProjectTeamMasterSchedulesAsync,
} from '@masterSchedule/masterSchedule.queries';
import { ALL_USERS_TEAM_NAME, FETCH_QUERIES_NAME } from '@common/constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import { ETeamFormKeys, ITeamFormState } from './teamModal.models';
import './TeamForm.scss';
import { toProjectUserProfileOption } from '@userProfile/projectUserProfile.services';
import usePrompt from '@common/prompt.hooks';
import { IProjectUserProfileOption } from '@userProfile/projectUserProfile.models';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { useQueryClient } from '@tanstack/react-query';
import { AssignT1Team } from './AssignT1Team';

export interface ITeamFormProps {
  projectId: number;
  team?: IProjectTeam;
  companyOptions?: IOption<IProjectCompanyOption>[];
  defaultCompany?: IOption<IProjectCompanyOption>;
  useFormMethods: UseFormReturn;
  errorUpdateProjectTeam: AxiosErrorResponse | null;
  errorCreateProjectTeam: AxiosErrorResponse | null;
}

export const TeamForm: React.FunctionComponent<ITeamFormProps> = ({
  projectId,
  team,
  defaultCompany,
  useFormMethods,
  companyOptions = [],
  errorUpdateProjectTeam,
  errorCreateProjectTeam,
}) => {
  const queryClient = useQueryClient();
  const selectedCompany = useFormMethods.watch(ETeamFormKeys.COMPANY);

  const { data: companyMembers, isLoading: isLoadingCompanyMembers } = useGetProjectUserProfileOptionsByCompanyAsync(
    {
      projectId,
      companyId: selectedCompany?.data?.id || 0,
    },
    !useFormMethods.formState.isDirty,
  );

  const getDefaultValues = (
    teamLocal?: IProjectTeam,
    companyMembersLocal?: IPagedResultsV1<IProjectUserProfileOption>,
    defaultCompanyLocal?: IOption<IProjectCompanyOption>,
  ) => {
    const users =
      (teamLocal?.users?.length ?? 0) <= 0 ? [] : teamLocal?.users?.map((u) => toProjectUserProfileOption(u));
    const state: ITeamFormState = {
      [ETeamFormKeys.NAME]: teamLocal?.teamName || null,
      [ETeamFormKeys.ASSIGN_MEMBERS]: users ?? [],
      [ETeamFormKeys.COMPANY]: defaultCompanyLocal ?? null,
    };
    return state;
  };

  const { data: dataProjectMasterScheduleOptionsForAssignT1Teams } =
    useGetProjectMasterScheduleOptionsForAssignT1TeamsAsync(
      {
        projectId: team?.projectId as number,
        projectCompanyId: selectedCompany?.data?.id,
      },
      !useFormMethods.formState.isDirty && !!selectedCompany?.data?.id,
    );

  const {
    mutate,
    error: errorUpdateProjectTeamMasterSchedule,
    isSuccess: isSuccessUpdateProjectTeamMasterSchedule,
    reset: resetUpdateProjectTeamMasterSchedule,
  } = useUpsertProjectTeamMasterSchedulesAsync();

  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessUpdateProjectTeamMasterSchedule,
            message: 'Update project team for master schedule success.',
            reset: resetUpdateProjectTeamMasterSchedule,
          },
        ]}
        callbackAfterToasted={() => {
          if (isSuccessUpdateProjectTeamMasterSchedule) {
            queryClient.invalidateQueries([
              projectId,
              selectedCompany?.data?.id,
              FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_ASSIGN_T1_TEAMS,
            ]);
          }
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreateProjectTeam, genericError: 'Create team failed' },
          { error: errorUpdateProjectTeam, genericError: 'Update team failed' },
          {
            error: errorUpdateProjectTeamMasterSchedule,
            genericError: 'Error updating the project team for master schedule',
          },
        ]}
      />
      <Form
        defaultValues={{
          callback: () => {
            const state = getDefaultValues(team, companyMembers, defaultCompany);
            return state;
          },
          deps: [team, companyMembers, defaultCompany],
        }}
        enableUnsavedChangesPrompt
        unsavedChangesPromptProps={{
          usePrompt,
        }}
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ETeamFormKeys.COMPANY} text="Company" required />
                <FormSelect
                  options={companyOptions}
                  formFieldMethods={formFieldMethods}
                  id={ETeamFormKeys.COMPANY}
                  rules={requiredRule}
                  allowSearch={false}
                  clearable={false}
                  disabled={!!team}
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ETeamFormKeys.NAME} text="Name" required />
                <FormTextField
                  formFieldMethods={formFieldMethods}
                  id={ETeamFormKeys.NAME}
                  rules={requiredRule}
                  disabled={!!team}
                />
              </Grid>
              <Grid item xs={12}>
                <div className="gcl-gems-team-form-assign-member-label-container">
                  <LabelWithMessage labelFor={ETeamFormKeys.ASSIGN_MEMBERS} text="Assign Members" />
                  {/* <Icon iconName="PersonPlus" className="gcl-gems-team-form-assign-member-add-icon" /> */}
                </div>
                <ContentLoader
                  active={isLoadingCompanyMembers}
                  linePadding={10}
                  paragraphConfigs={[{ suffixBlankRow: false, numberOfRows: 5, lineHeight: 10 }]}
                >
                  <FormCheckboxGroup
                    itemPerRow={2}
                    allowSelectAll
                    checkOptions={companyMembers?.items || []}
                    displayFieldName="displayName"
                    formFieldMethods={formFieldMethods}
                    id={ETeamFormKeys.ASSIGN_MEMBERS}
                    checkIsSelected={(option1: any, option2: any) => {
                      return option1.upn === option2.upn;
                    }}
                    disabled={team?.teamName === ALL_USERS_TEAM_NAME}
                  />
                </ContentLoader>
              </Grid>
              <AssignT1Team
                allTeams={dataProjectMasterScheduleOptionsForAssignT1Teams?.projectTeams ?? []}
                assignT1Teams={dataProjectMasterScheduleOptionsForAssignT1Teams?.projectMasterSchedules || []}
                onChangeAssignT1Teams={(items) => {
                  mutate(items);
                }}
              />
            </Grid>
          );
        }}
      />
    </>
  );
};
